export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CropOptions {
  /** Largura máxima (redimensiona para caber) */
  maxWidth?: number;
  /** Altura máxima (redimensiona para caber) */
  maxHeight?: number;
  /** Qualidade JPEG 0-1 (menor = arquivo menor) */
  quality?: number;
}

export function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });
}

/**
 * Redimensiona mantendo proporção para caber em maxWidth x maxHeight
 */
function getResizedDimensions(
  width: number,
  height: number,
  maxWidth?: number,
  maxHeight?: number
): { width: number; height: number } {
  if (!maxWidth && !maxHeight) return { width, height };
  let w = width;
  let h = height;
  if (maxWidth && w > maxWidth) {
    h = (h * maxWidth) / w;
    w = maxWidth;
  }
  if (maxHeight && h > maxHeight) {
    w = (w * maxHeight) / h;
    h = maxHeight;
  }
  return { width: Math.round(w), height: Math.round(h) };
}

export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  fileName = "cropped.jpg",
  options: CropOptions = {}
): Promise<File> {
  const maxW = options.maxWidth ?? 1920;
  const maxH = options.maxHeight ?? 1080;
  const quality = options.quality ?? 0.82;

  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Canvas context not available");
  }

  const { width: outWidth, height: outHeight } = getResizedDimensions(
    pixelCrop.width,
    pixelCrop.height,
    maxW,
    maxH
  );

  canvas.width = outWidth;
  canvas.height = outHeight;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outWidth,
    outHeight
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to create blob"));
          return;
        }
        const file = new File([blob], fileName, {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
        resolve(file);
      },
      "image/jpeg",
      quality
    );
  });
}

/**
 * Redimensiona uma imagem (File) para reduzir tamanho. Mantém proporção.
 */
export async function resizeImage(
  file: File,
  options: CropOptions = {}
): Promise<File> {
  const maxW = options.maxWidth ?? 1920;
  const maxH = options.maxHeight ?? 1080;
  const quality = options.quality ?? 0.82;
  const src = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const image = await createImage(src);
  const { width: outWidth, height: outHeight } = getResizedDimensions(
    image.width,
    image.height,
    maxW,
    maxH
  );
  const canvas = document.createElement("canvas");
  canvas.width = outWidth;
  canvas.height = outHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context not available");
  ctx.drawImage(image, 0, 0, outWidth, outHeight);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to create blob"));
          return;
        }
        const name = file.name.replace(/\.[^.]+$/, "") || "image";
        resolve(
          new File([blob], `${name}.jpg`, {
            type: "image/jpeg",
            lastModified: Date.now(),
          })
        );
      },
      "image/jpeg",
      quality
    );
  });
}
